import { Auth } from "src/domains/shared/utils/amplifyCompat";
import { useRouter } from "next/router";
import { FunctionComponent, useEffect, useState } from "react";
import BigLoadingIndicator from "../../components/BigLoaingIndicator";
import { SITE_APP_START_HREF } from "../../components/Home/SiteShell/SiteHeader/siteNav";
import { isLoginClaimPending } from "../../utils/exclusiveLogin/loginClaimPending";
import { LOGIN_RETURN_QUERY, resolveLoginReturnPath } from "../../utils/loginReturnPath";
import { EmotionJSX } from "node_modules/@emotion/react/dist/declarations/src/jsx-namespace";

type SignInCheck = "checking" | "signedIn" | "signedOut";

/**
 * 이 브라우저의 로그인으로 사용자 정보를 읽을 수 있고, 그 로그인이 점유 확인을 기다리는 중이 아닌가. 점유 대기는 응답
 * 뒤에 본다 — 조회하는 동안 다른 탭이 점유를 시작했을 수 있다.
 */
const isSignedIn = async (): Promise<boolean> => {
  try {
    const user = await Auth.currentUserInfo();
    return Boolean(user) && !isLoginClaimPending();
  } catch {
    return false;
  }
};

/**
 * 로그인하지 않은 사람이 쓰는 화면(로그인·비밀번호 찾기)의 가드. 로그인한 사람에게는 오류 화면 없이 진행 표시만 보이고,
 * 돌아갈 주소(없으면 프로젝트 목록)로 방문 기록을 바꿔 옮긴다(marketing-auth-pages 「로그인한 사용자는 인증 화면을 거치지
 * 않는다」).
 */
const withNotAuth = (WrappedComponent: FunctionComponent<{}>) => {
  // eslint-disable-next-line react/display-name
  return (props: EmotionJSX.IntrinsicAttributes) => {
    const router = useRouter();
    const [signInCheck, setSignInCheck] = useState<SignInCheck>("checking");

    // 마운트 때 한 번만 판정한다 — 로그인을 마친 뒤 다시 판정하면 로그인 마무리의 이동과 겨룬다.
    useEffect(() => {
      let unmounted = false;
      void isSignedIn().then((signedIn) => {
        if (!unmounted) setSignInCheck(signedIn ? "signedIn" : "signedOut");
      });
      return () => {
        unmounted = true;
      };
    }, []);

    // 정적 최적화 페이지라 라우터가 준비되기 전에는 쿼리(`returnTo`)가 비어 있다.
    useEffect(() => {
      if (signInCheck !== "signedIn" || !router.isReady) return;
      void router.replace(
        resolveLoginReturnPath(router.query[LOGIN_RETURN_QUERY], SITE_APP_START_HREF)
      );
    }, [signInCheck, router]);

    if (signInCheck === "signedOut") return <WrappedComponent {...props} />;
    return <BigLoadingIndicator description="페이지 불러오는 중..." />;
  };
};

export default withNotAuth;
